import logo from "../fortinet/logo.svg";
import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";

export const fortiManager = defineConnector({
  identifier: "fortiManager",
  name: "FortiManager",
  tagLine: "Centralized Fortinet firewall management",
  keywords: "Fortinet FortiGate FortiAP firewall access points",
  logo,
  protocol: "http",
  tags: ["security", "it"],
  defaultUrlPattern: ({ host }) =>
    exactUrlToRegex(host).replace(/\/\.\*$/, "/jsonrpc(?:\\?.*)?$"),
  auth: (app) => bearerAuth(app.secret.apiToken),
  steps: [
    {
      title: "Create a REST API administrator",
      items: [
        {
          kind: "markdown",
          content:
            "Requires FortiManager 7.2.2 or later. Go to **System Settings > Administrators**, select **Create New > REST API Admin**, and assign an admin profile with permissions for your workflows. Set **JSON API Access** to **Read-Write** to update configurations, or **Read** for read-only workflows. Configure **Trusted Hosts** to allow the IP addresses your API requests originate from, restrict access to the required ADOMs, and copy the generated API key.",
        },
      ],
    },
    {
      title: "Enter your FortiManager details",
      items: [
        {
          kind: "hostname",
          label: "FortiManager host",
          required: true,
          placeholder: "fortimanager.example.com",
          helperText:
            "Your FortiManager hostname or IP address, including the port if it differs from 443.",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "API key",
          required: true,
          target: { kind: "secrets", field: "apiToken" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ host }) => ({
      url: `https://${host}/jsonrpc`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: 1,
        method: "get",
        params: [{ url: "/sys/status" }],
      }),
      expectedBodyContains: '"OK"',
    }),
  },
  usageHint: ({ host }) =>
    `Send JSON-RPC requests as HTTP POST to https://${host}/jsonrpc with Content-Type: application/json. Specify the method and params in the JSON body, for example {"id":1,"method":"get","params":[{"url":"/sys/status"}]}. Authentication is supplied by the connector; no login or session field is needed. Check result[].status.code: 0 means success, and API errors can still return HTTP 200.`,
});
